"use client"

import { createContext, useContext, useEffect } from "react"
import { useAppDispatch, useAppSelector } from "../store/hooks"
import {
  fetchCart,
  updateCartItem,
  applyCoupon,
  selectCartItems,
  selectCartPrice,
  selectCartLoading,
} from "../store/slices/cartSlice"
import {
  fetchWishlist,
  addToWishlist,
  removeFromWishlist,
  selectWishlistItems,
  selectWishlistLoading,
} from "../store/slices/wishlistSlice"

const CartContext = createContext(null)

export const CartProvider = ({ children }) => {
  const dispatch = useAppDispatch()

  // Cart selectors
  const cartItems = useAppSelector(selectCartItems)
  const cartPrice = useAppSelector(selectCartPrice)
  const cartLoading = useAppSelector(selectCartLoading)

  // Wishlist selectors
  const wishlistItems = useAppSelector(selectWishlistItems)
  const wishlistLoading = useAppSelector(selectWishlistLoading)

  // Load cart and wishlist on mount
  useEffect(() => {
    dispatch(fetchCart())
    dispatch(fetchWishlist())
  }, [dispatch])

  // Cart actions
  const addToCart = (productId, quantity = 1, redirectToCart = false) => {
    return dispatch(
      updateCartItem({
        productId,
        quantity,
        type: "add",
        redirectToCart,
      }),
    )
  }

  const updateCart = (productId, quantity) => {
    return dispatch(
      updateCartItem({
        productId,
        quantity,
        type: "update",
      }),
    )
  }

  const removeFromCart = (productId) => {
    return dispatch(
      updateCartItem({
        productId,
        quantity: 0,
        type: "update",
      }),
    )
  }

  const applyCouponCode = (couponCode) => {
    return dispatch(applyCoupon(couponCode))
  }

  // Wishlist actions
  const addItemToWishlist = (productId) => {
    return dispatch(addToWishlist(productId))
  }

  const removeItemFromWishlist = (productId) => {
    return dispatch(removeFromWishlist(productId))
  }

  // Helper functions
  const isInCart = (productId) => {
    if (!productId || !cartItems) return false
    return cartItems.some((item) => item.product?.id === productId)
  }

  const getCartQuantity = (productId) => {
    if (!productId || !cartItems) return 0
    const item = cartItems.find((item) => item.product?.id === productId)
    return item ? item.quantity : 0
  }

  const isInWishlist = (productId) => {
    if (!productId || !wishlistItems) return false
    return wishlistItems.some((item) => item.id === productId)
  }

  const value = {
    // Cart state
    cartItems,
    cartPrice,
    cartLoading,

    // Wishlist state
    wishlistItems,
    wishlistLoading,

    // Cart actions
    addToCart,
    updateCart,
    removeFromCart,
    applyCouponCode,

    // Wishlist actions
    addItemToWishlist,
    removeItemFromWishlist,

    // Helper functions
    isInCart,
    getCartQuantity,
    isInWishlist,
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (context === null) {
    throw new Error("useCart must be used within a CartProvider")
  }
  return context
}
